<template>
  <div class="container box_component supervisor-panel">
    <div class="header-title">
      <h5 class="panel-title">{{ modeLabel }}</h5>
      <div class="header-actions">
        <button
          class="compare-rounds-btn"
          type="button"
          :disabled="!canOpenCompareModal"
          @click="openCompareModal"
        >
          <i class="fa-solid fa-columns"></i> {{ $t('training.supervisor.compare_launch') || 'Comparar rondas' }}
        </button>
        <button class="new-round-btn" @click="openCreateModal">
          <i class="fa-solid fa-plus"></i> {{ $t('training.btn_new') }}
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <label class="muted" for="judgeFilter">{{ $t('training.supervisor.filter_label') }}</label>
      <select
        id="judgeFilter"
        v-model="selectedJudgeId"
        class="input select"
        :disabled="isJudgeFilterLocked"
        @change="onJudgeFilterChange"
      >
        <option value="">{{ $t('training.supervisor.filter_placeholder') }}</option>
        <option v-for="judge in judgeOptions" :key="judge.id" :value="judge.id">
          {{ judgeLabel(judge) }}
        </option>
      </select>

      <label class="muted" for="roundFilter">{{ $t('training.supervisor.round_filter_label') || 'Filtrar por ronda' }}</label>
      <select
        id="roundFilter"
        v-model="selectedRoundId"
        class="input select"
        :disabled="isRoundFilterLocked"
        @change="onRoundFilterChange"
      >
        <option value="">{{ $t('training.supervisor.round_filter_placeholder') || 'Selecciona una ronda base' }}</option>
        <option v-for="round in roundBaseOptions" :key="round.id" :value="round.id">
          {{ round.label }}
        </option>
      </select>

      <button class="btn-clear-filters" type="button" @click="clearDashboardFilters">
        {{ $t('training.supervisor.clear_filters') || 'Limpiar filtros' }}
      </button>
    </div>

    <div class="dashboard-panel">
      <div class="dashboard-toolbar">
        <h6 class="dashboard-title">{{ $t('training.supervisor.dashboard_title') || 'Panel de control' }}</h6>
        <div class="dashboard-filters" v-if="showAdvancedDashboardFilters">
          <input
            v-model="dashboardFilters.from"
            type="date"
            class="input dashboard-input"
            :placeholder="$t('training.supervisor.dashboard_from') || 'Desde'"
          />
          <input
            v-model="dashboardFilters.to"
            type="date"
            class="input dashboard-input"
            :placeholder="$t('training.supervisor.dashboard_to') || 'Hasta'"
          />
          <select v-model="dashboardFilters.groupBy" class="input dashboard-input dashboard-select">
            <option value="day">{{ $t('training.supervisor.group_day') || 'Día' }}</option>
            <option value="week">{{ $t('training.supervisor.group_week') || 'Semana' }}</option>
            <option value="month">{{ $t('training.supervisor.group_month') || 'Mes' }}</option>
          </select>
            <button class="btn-refresh" type="button" @click="refreshSupervisorData">
            {{ $t('training.supervisor.dashboard_refresh') || 'Actualizar panel' }}
          </button>
        </div>
      </div>

      <p class="muted" v-if="dashboardLoading">{{ $t('training.loading') }}</p>

      <template v-else>
        <div class="active-filters" v-if="selectedJudgeFilterLabel || selectedRoundFilterLabel">
          <span v-if="selectedJudgeFilterLabel" class="active-filter-chip">
            {{ $t('training.supervisor.viewing_judge') || 'Viendo juez' }}: {{ selectedJudgeFilterLabel }}
          </span>
          <span v-if="selectedRoundFilterLabel" class="active-filter-chip">
            {{ $t('training.supervisor.round_filter_label') || 'Filtrar por ronda' }}: {{ selectedRoundFilterLabel }}
          </span>
        </div>

        <div v-if="selectedRoundId" class="dashboard-block views-block">
          <h6>{{ $t('training.supervisor.views_title') || 'Control de vistas de la ronda' }}</h6>

          <p class="muted" v-if="viewStatsLoading">{{ $t('training.loading') }}</p>

          <template v-else>
            <div class="views-summary-grid">
              <div class="summary-card">
                <span class="summary-label">{{ $t('training.supervisor.views_total') || 'Vistas totales' }}</span>
                <strong>{{ viewStatsTotal }}</strong>
              </div>
              <div class="summary-card">
                <span class="summary-label">{{ $t('training.supervisor.views_unique') || 'Usuarios únicos' }}</span>
                <strong>{{ viewStatsUnique }}</strong>
              </div>
            </div>

            <div class="views-two-cols">
              <div>
                <p class="muted views-subtitle">{{ $t('training.supervisor.views_timeline') || 'Timeline de vistas' }}</p>
                <div v-if="viewStatsTimeline.length" class="views-list-scroll">
                  <div class="views-line" v-for="(point, index) in viewStatsTimeline.slice(0, 10)" :key="`vs-tl-${index}`">
                    <span>{{ point.period || point.label || point.date || '—' }}</span>
                    <strong>{{ point.views ?? point.total ?? point.count ?? 0 }}</strong>
                  </div>
                </div>
                <p v-else class="muted">{{ $t('training.supervisor.no_timeline') || 'Sin datos de timeline' }}</p>
              </div>

              <div>
                <p class="muted views-subtitle">{{ $t('training.supervisor.views_recent') || 'Últimas vistas' }}</p>
                <div v-if="viewStatsRecent.length" class="views-list-scroll">
                  <div class="views-line" v-for="(entry, index) in viewStatsRecent.slice(0, 10)" :key="`vs-rv-${index}`">
                    <span>{{ formatViewActor(entry) }}</span>
                    <strong>{{ formatViewDate(entry) }}</strong>
                  </div>
                </div>
                <p v-else class="muted">{{ $t('training.supervisor.no_recent_views') || 'Sin vistas recientes' }}</p>
              </div>
            </div>
          </template>
        </div>

        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-label">{{ $t('training.supervisor.total') || 'Total' }}</span>
            <strong>{{ dashboardSummary.total || 0 }}</strong>
          </div>
          <div class="summary-card success">
            <span class="summary-label">{{ $t('training.supervisor.completed') || 'Completadas' }}</span>
            <strong>{{ dashboardSummary.completed || 0 }}</strong>
          </div>
          <div class="summary-card warning">
            <span class="summary-label">{{ $t('training.supervisor.in_scoring') || 'En scoring' }}</span>
            <strong>{{ dashboardSummary.in_scoring || 0 }}</strong>
          </div>
          <div class="summary-card pending">
            <span class="summary-label">{{ $t('training.supervisor.pending') || 'Pendientes' }}</span>
            <strong>{{ dashboardSummary.pending || 0 }}</strong>
          </div>
        </div>

        <div class="dashboard-main-row">
          <div class="dashboard-block selected-judge-stats">
            <h6>{{ $t('training.supervisor.rankings') || 'Comparativas' }}</h6>
            <div class="selected-stats-grid">
              <div class="selected-stat-item">
                <span>{{ $t('training.supervisor.best_duration') || 'Menor duración' }}</span>
                <button
                  type="button"
                  class="extreme-trigger"
                  @click="openJudgeInfoFromExtreme('fastest_round')"
                >
                  {{ extremeJudgeMetricLabel('fastest_round', 'duration') }}
                </button>
              </div>
              <div class="selected-stat-item">
                <span>{{ $t('training.supervisor.worst_duration') || 'Mayor duración' }}</span>
                <button
                  type="button"
                  class="extreme-trigger"
                  @click="openJudgeInfoFromExtreme('slowest_round')"
                >
                  {{ extremeJudgeMetricLabel('slowest_round', 'duration') }}
                </button>
              </div>
              <div class="selected-stat-item">
                <span>{{ $t('training.supervisor.best_score') || 'Mayor puntaje' }}</span>
                <button
                  type="button"
                  class="extreme-trigger"
                  @click="openJudgeInfoFromExtreme('highest_total_score_round')"
                >
                  {{ extremeJudgeMetricLabel('highest_total_score_round', 'score') }}
                </button>
              </div>
              <div class="selected-stat-item">
                <span>{{ $t('training.supervisor.worst_score') || 'Menor puntaje' }}</span>
                <button
                  type="button"
                  class="extreme-trigger"
                  @click="openJudgeInfoFromExtreme('lowest_total_score_round')"
                >
                  {{ extremeJudgeMetricLabel('lowest_total_score_round', 'score') }}
                </button>
              </div>
            </div>
          </div>

          <div class="dashboard-block assigned-judges-block">
            <h6>{{ $t('training.supervisor.assigned_judges') || 'Jueces con rondas asignadas' }}</h6>
            <div v-if="dashboardAssignedJudges.length" class="assigned-judges-list" :class="{ 'round-assigned-judges-list': selectedRoundId }">
              <template v-if="selectedRoundId">
                <div
                  v-for="judge in dashboardAssignedJudges"
                  :key="judge.id"
                  class="assigned-judge-pill"
                >
                  <div class="assigned-judge-pill__block assigned-judge-pill__identity">
                    <span class="assigned-judge-pill__name">{{ judgeName(judge) || judgeLabel(judge) }}</span>
                    <span class="assigned-judge-pill__id">({{ judge.id }})</span>
                  </div>
                  <div class="assigned-judge-pill__block assigned-judge-pill__value">
                    {{ formatScore(judge.total_score) }}
                  </div>
                  <div class="assigned-judge-pill__block assigned-judge-pill__value">
                    {{ formatDuration(judge.total_time) }}
                  </div>
                </div>
              </template>
              <template v-else>
              <button
                v-for="judge in dashboardAssignedJudges"
                :key="judge.id"
                type="button"
                class="assigned-judge-chip"
                :class="{
                  'assigned-judge-chip--complete': Number(judge.assigned_rounds_count || 0) > 0 && Number(judge.resolved_rounds_count || 0) >= Number(judge.assigned_rounds_count || 0),
                  'assigned-judge-chip--pending': Number(judge.resolved_rounds_count || 0) < Number(judge.assigned_rounds_count || 0)
                }"
                @click="selectJudgeFromDashboard(judge.id)"
              >
                <span class="assigned-judge-chip__name">{{ judgeLabel(judge) }}</span>
                <span class="assigned-judge-chip__stats">
                  {{ Number(judge.resolved_rounds_count || 0) }}/{{ Number(judge.assigned_rounds_count || 0) }}
                </span>
              </button>
              </template>
            </div>
            <p v-else class="muted">{{ $t('training.supervisor.no_assigned_judges') || 'Sin jueces con rondas asignadas' }}</p>
          </div>
        </div>
      </template>

      <div v-if="floatingJudgeInfo" class="floating-overlay" @click.self="closeFloatingJudgeInfo">
        <div class="floating-panel">
          <div class="floating-header">
            <h6>{{ $t('training.supervisor.judge_info_title') || 'Información del juez' }}</h6>
            <button type="button" class="floating-close" @click="closeFloatingJudgeInfo">×</button>
          </div>
          <div class="floating-body">
            <p><strong>{{ $t('training.supervisor.filter_label') || 'Juez' }}:</strong> {{ judgeLabel(floatingJudgeInfo.judge) }}</p>
            <p><strong>Email:</strong> {{ floatingJudgeInfo.judge.email || '—' }}</p>
            <p><strong>{{ floatingJudgeInfo.metricTitle }}:</strong> {{ floatingJudgeInfo.metricLabel }}</p>
            <p><strong>{{ $t('training.round_name') }}:</strong> {{ floatingJudgeInfo.round_name || '—' }}</p>
          </div>
        </div>
      </div>
    </div>

    <p class="muted" v-if="isLoading">{{ $t('training.loading') }}</p>
    <p class="muted" v-else-if="!visibleRounds.length">{{ $t('training.supervisor.empty') }}</p>

    <div class="training-grid" v-else :class="visibleRounds.length <= 2 ? 'single' : ''">
      <div
        v-for="round in visibleRounds"
        :key="round.id"
        class="training-card"
        :class="getStatusClass(round.status_id)"
        @click.prevent="viewRound(round.id)"
      >
        <div class="card-header" role="button" tabindex="0">
          <img :src="resolveRoundVideoLink(round) ? youtubeVideo : notVideo" alt="Round thumbnail" class="card-image">
        </div>
        <div class="card-body" role="button" tabindex="0">
          <p class="status-text">{{ resolveRoundStatusLabel(round) }}</p>
          <p><strong>{{ $t('training.rules') }}:</strong> {{ resolveRoundRuleLabel(round) }}</p>
          <p><strong>{{ $t('training.round_name') }}:</strong> {{ round.round_name || '—' }}</p>
          <p><strong>{{ $t('training.total_score') }}:</strong> {{ formatScore(round.total_score) }}</p>
          <p v-if="resolveCompareJudge(round)"><strong>{{ $t('training.assigned_judge') }}:</strong> {{ judgeName(resolveCompareJudge(round)) }}</p>
          <p v-else-if="round.assigned_judge_id || round.judge_id"><strong>{{ $t('training.assigned_judge') }}:</strong> #{{ round.assigned_judge_id || round.judge_id }}</p>
          <div v-if="getViewControlEntries(round).length" class="view-control-list">
            <p v-for="entry in getViewControlEntries(round)" :key="`${round.id}-${entry.key}`" class="view-control-info">
              <strong>{{ entry.label }}:</strong> {{ entry.dateLabel }}
            </p>
          </div>

        </div>
        <div class="card-footer" @click.stop>
          <button
            class="action-btn edit"
            type="button"
            :title="$t('training.btn_edit')"
            @click.prevent="editRound(round.id)"
          >
            <i class="fa-solid fa-pencil-alt"></i>
          </button>
          <button
            class="action-btn delete"
            type="button"
            :title="$t('training.btn_delete')"
            @click.prevent="deleteRound(round.id)"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-if="showVisiblePagination" class="rounds-pagination">
      <button
        type="button"
        class="pagination-btn"
        :disabled="visiblePagination.current_page <= 1 || isLoading || dashboardLoading"
        @click="changeVisiblePage(visiblePagination.current_page - 1)"
      >
        {{ $t('training.pagination.prev') || 'Anterior' }}
      </button>

      <span class="pagination-summary">
        {{ $t('training.pagination.page') || 'Página' }} {{ visiblePagination.current_page }}
        {{ $t('training.pagination.of') || 'de' }} {{ visiblePagination.last_page }}
      </span>

      <button
        type="button"
        class="pagination-btn"
        :disabled="visiblePagination.current_page >= visiblePagination.last_page || isLoading || dashboardLoading"
        @click="changeVisiblePage(visiblePagination.current_page + 1)"
      >
        {{ $t('training.pagination.next') || 'Siguiente' }}
      </button>
    </div>

    <ConfirmationModal
      ref="confirmationModal"
      :message="modalMessage"
      :showCancel="confirmationModalConfig.showCancel"
      :showRuleSelector="confirmationModalConfig.showRuleSelector"
      :ruleOptions="ruleOptions"
      :showNameInput="confirmationModalConfig.showNameInput"
      :showJudgeSelect="confirmationModalConfig.showJudgeSelect"
      :showVideoInput="confirmationModalConfig.showVideoInput"
      :showVideoSourceSwitch="confirmationModalConfig.showVideoSourceSwitch"
      :showExternalAthleteSelect="confirmationModalConfig.showExternalAthleteSelect"
      :externalAthleteOptions="externalAthleteOptions"
      :externalAthletesLoading="externalAthletesLoading"
      :judgeOptions="judgeOptions"
      :showRoundSourceSelect="confirmationModalConfig.showRoundSourceSelect"
      :competitionRounds="competitionRounds"
      @confirmed="handleConfirmed"
      @canceled="cancelModal"
    />
    <TrainingComparePanel
      :visible="compareVisible"
      :judge-groups="compareRoundGroups"
      :original-groups="compareOriginalRoundGroups"
      :original-options="compareOriginalRoundOptions"
      :mode="compareMode"
      :left-round-id="compareSelection.leftRoundId"
      :right-round-id="compareSelection.rightRoundId"
      :payload="comparePayload"
      :loading="compareLoading"
      :error="compareError"
      @close="closeCompareModal"
      @update-mode="setCompareMode"
      @select-left="setCompareSide('left', $event)"
      @select-right="setCompareSide('right', $event)"
      @compare="fetchCompareRounds"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ConfirmationModal from '@/components/competitions/ConfirmationModal.vue';
import TrainingComparePanel from '@/components/training/TrainingComparePanel.vue';
import router from '@/router/router';

export default {
  name: 'SupervisorTrainingPanel',
  components: { ConfirmationModal, TrainingComparePanel },
  props: {
    modeLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      notVideo: require('@/assets/videoOff.jpg'),
      youtubeVideo: require('@/assets/videoOn.jpg'),
      rounds: [],
      isLoading: false,
      selectedJudgeId: '',
      selectedRoundId: '',
      modalMessage: '',
      pendingModalAction: null,
      pendingModalPayload: null,
      confirmationModalConfig: {
        showCancel: true,
        showRuleSelector: false,
        showNameInput: false,
        showJudgeSelect: false,
        showVideoInput: false,
        showVideoSourceSwitch: false,
        showExternalAthleteSelect: false,
        showRoundSourceSelect: false,
      },
      ruleOptions: [
        { id: 1, name: 'Without rules' },
        { id: 2, name: 'TJS v2.0' },
        { id: 3, name: 'TJS v3.0' },
        { id: 4, name: 'TJS v4.0' }
      ],
      isSubmitting: false,
      judgeOptions: [],
      dashboardLoading: false,
      showAdvancedDashboardFilters: false,
      dashboardSummary: {},
      dashboardExtremes: {},
      dashboardAssignedJudges: [],
      dashboardItems: [],
      selectorRounds: [],
      selectorRoundsLoading: false,
      selectorRoundsError: '',
      competitionRounds: [],
      externalAthleteOptions: [],
      externalAthletesLoading: false,
      dashboardScope: null,
      floatingJudgeInfo: null,
      judgePage: 1,
      roundPage: 1,
      judgePagination: null,
      roundPagination: null,
      viewStatsLoading: false,
      viewStatsSummary: {},
      viewStatsTimeline: [],
      viewStatsRecent: [],
      compareVisible: false,
      compareLoading: false,
      compareError: '',
      compareMode: 'judge',
      comparePayload: null,
      compareSelection: {
        leftRoundId: '',
        rightRoundId: '',
        leftJudgeId: null,
        rightJudgeId: null,
      },
      dashboardFilters: {
        from: '',
        to: '',
        groupBy: 'week',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      },
    };
  },
  computed: {
    ...mapState('user', ['token']),
    selectedJudgeFilterLabel() {
      if (!this.selectedJudgeId) return '';
      const selectedId = Number(this.selectedJudgeId);
      const selected = this.judgeOptions.find(judge => Number(judge.id) === selectedId);
      if (!selected) return `#${this.selectedJudgeId}`;
      return this.judgeLabel(selected);
    },
    isJudgeFilterLocked() {
      return !!this.selectedRoundId;
    },
    isRoundFilterLocked() {
      return !!this.selectedJudgeId;
    },
    viewStatsTotal() {
      return this.viewStatsSummary?.total_views
        ?? this.viewStatsSummary?.total
        ?? this.viewStatsSummary?.views
        ?? 0;
    },
    viewStatsUnique() {
      return this.viewStatsSummary?.unique_users
        ?? this.viewStatsSummary?.unique_viewers
        ?? this.viewStatsSummary?.unique
        ?? 0;
    },
    visibleRounds() {
      if (this.selectedJudgeId || this.selectedRoundId) {
        return Array.isArray(this.dashboardItems) ? this.dashboardItems : [];
      }

      return Array.isArray(this.rounds) ? this.rounds : [];
    },
    visiblePagination() {
      if (this.selectedJudgeId || this.selectedRoundId) {
        return this.roundPagination;
      }

      return null;
    },
    showVisiblePagination() {
      return Boolean(this.visiblePagination && Number(this.visiblePagination.last_page) > 1);
    },
    selectedRoundFilterLabel() {
      if (!this.selectedRoundOption) return '';
      return this.selectedRoundOption.label || `#${this.selectedRoundId}`;
    },
    selectedRoundOption() {
      if (!this.selectedRoundId) return null;
      const selectedId = String(this.selectedRoundId);
      return (this.selectorRounds || []).find(round => String(round.selector_id) === selectedId) || null;
    },
    selectedRoundFilterValue() {
      const selected = this.selectedRoundOption;
      if (!selected) return '';

      const latestRoundId = Number(selected.latest_round_id || 0);
      if (latestRoundId) return latestRoundId;

      const fallbackRoundId = Array.isArray(selected.round_ids)
        ? Number(selected.round_ids[0] || 0)
        : 0;

      return fallbackRoundId || '';
    },
    canOpenCompareModal() {
      const hasJudgeVsJudge = this.compareRoundGroups.some(group => Array.isArray(group.options) && group.options.length > 1);
      if (hasJudgeVsJudge) return true;

      const originalReferenceIds = new Set(
        this.compareOriginalRoundGroups
          .map(group => Number(group.referenceRoundId || 0))
          .filter(Boolean)
      );

      return this.compareRoundGroups.some(group => (
        Array.isArray(group.options)
        && group.options.length
        && originalReferenceIds.has(Number(group.referenceRoundId || 0))
      ));
    },
    compareRoundGroups() {
      return this.buildCompareGroupsFromSelectors()
        .map((group) => ({
          ...group,
          options: [...group.options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        }))
        .filter(group => group.options.length)
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    },
    compareRoundOptions() {
      const optionMap = new Map();
      this.compareRoundGroups.flatMap(group => group.options).forEach((option) => {
        if (!option) return;

        if (optionMap.has(option.id)) {
          optionMap.set(option.id, {
            ...option,
            ...optionMap.get(option.id),
          });
          return;
        }

        optionMap.set(option.id, option);
      });

      return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    },
    compareOriginalRoundGroups() {
      return this.buildCompareGroupsFromOriginalRounds()
        .map((group) => ({
          ...group,
          options: [...group.options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        }))
        .filter(group => group.options.length)
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    },
    compareOriginalRoundOptions() {
      const optionMap = new Map();

      this.compareOriginalRoundGroups.flatMap(group => group.options).forEach((option) => {
        if (!option) return;

        if (optionMap.has(option.id)) {
          optionMap.set(option.id, {
            ...option,
            ...optionMap.get(option.id),
          });
          return;
        }

        optionMap.set(option.id, option);
      });

      return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    },
    roundBaseOptions() {
      return (this.selectorRounds || []).map((item) => ({
        id: String(item.selector_id),
        label: item.label || item.round_name || `${this.$t('training.round_name') || 'Ronda'} #${item.selector_id}`,
      }));
    }
  },
  mounted() {
    this.refreshSupervisorData();
  },
  methods: {
    ...mapActions('competition', ['updateAppNavigationZone']),
    ...mapActions('training', ['setTrainingRoundsIdToView', 'setTrainingRoundsIdToEdit']),
    async fetchSelectorRounds() {
      this.selectorRoundsLoading = true;
      this.selectorRoundsError = '';
      try {
        const params = {
          timezone: this.dashboardFilters.timezone,
          status_id: 8,
        };

        if (this.selectedJudgeId) params.judge_id = this.selectedJudgeId;
        if (this.dashboardFilters.from) params.from = this.dashboardFilters.from;
        if (this.dashboardFilters.to) params.to = this.dashboardFilters.to;

        const response = await this.$axios.get('/training/selector-rounds', {
          params,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        const payload = response?.data;
        const items = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.data) ? payload.data : []);

        this.selectorRounds = items;
      } catch (error) {
        console.error('❌ Error al obtener selector de rondas:', error);
        this.selectorRounds = [];
        this.selectorRoundsError = error?.response?.data?.message || 'No se pudo cargar el selector de rondas.';
      } finally {
        this.selectorRoundsLoading = false;
      }
    },
    async fetchTrainingRounds() {
      if (!this.selectedJudgeId) {
        this.rounds = [];
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      try {
        const params = { type: 'judge' };
        params.judge_id = this.selectedJudgeId;
        const response = await this.$axios.get('/training/list', {
          params,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        this.rounds = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('❌ Error al obtener entrenamientos (supervisor):', error);
        this.rounds = [];
      } finally {
        this.isLoading = false;
      }
    },
    async fetchJudges() {
      try {
        const response = await this.$axios.get('/training/list', {
          params: { type: 'supervisor' },
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        const list = response.data?.data || response.data || [];
        this.judgeOptions = Array.isArray(list) ? list : [];
      } catch (error) {
        console.error('❌ Error al obtener jueces para supervisor:', error);
        this.judgeOptions = [];
      }
    },
    onJudgeFilterChange() {
      if (this.selectedJudgeId) {
        this.selectedRoundId = '';
      }
      this.roundPage = 1;
      this.refreshSupervisorData(1);
    },
    async onRoundFilterChange() {
      if (this.selectedRoundId) {
        this.selectedJudgeId = '';
        this.dashboardAssignedJudges = [];
        await this.fetchSelectorRounds();
      } else {
        this.clearViewStats();
        await this.fetchSelectorRounds();
      }
      this.roundPage = 1;
      await this.fetchSupervisorDashboard(1);
    },
    async clearDashboardFilters() {
      this.selectedJudgeId = '';
      this.selectedRoundId = '';
      this.roundPage = 1;
      this.dashboardFilters.from = '';
      this.dashboardFilters.to = '';
      this.rounds = [];
      this.dashboardAssignedJudges = [];
      await this.refreshSupervisorData(1);
    },
    async refreshSupervisorData(page = this.roundPage || 1) {
      const normalizedPage = Number(page) || 1;
      this.roundPage = normalizedPage;
      await Promise.all([
        this.fetchSelectorRounds(),
        this.fetchSupervisorDashboard(normalizedPage),
      ]);
    },
    async fetchSupervisorDashboard(page = this.roundPage) {
      this.dashboardLoading = true;
      try {
        const params = {
          type: 'supervisor',
          group_by: this.dashboardFilters.groupBy || 'week',
          timezone: this.dashboardFilters.timezone,
        };

        if (this.dashboardFilters.from) params.from = this.dashboardFilters.from;
        if (this.dashboardFilters.to) params.to = this.dashboardFilters.to;
        if (this.selectedJudgeId) params.judge_id = this.selectedJudgeId;
        if (this.selectedRoundFilterValue) params.selected_round_id = this.selectedRoundFilterValue;
        if (this.selectedJudgeId || this.selectedRoundFilterValue) params.page = Number(page) || 1;

        const response = await this.$axios.get('/training/list', {
          params,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        const payload = response?.data || {};
        const judges = payload?.data || [];
        const assignedRounds = payload?.assigned_rounds || {};

        this.judgeOptions = Array.isArray(judges) ? judges : this.judgeOptions;
        this.dashboardSummary = assignedRounds?.summary || {};
        this.dashboardExtremes = assignedRounds?.extremes || {};
        this.dashboardAssignedJudges = Array.isArray(assignedRounds?.assigned_judges)
          ? assignedRounds.assigned_judges
          : [];
        this.dashboardItems = Array.isArray(assignedRounds?.rounds)
          ? assignedRounds.rounds
          : (Array.isArray(assignedRounds?.items) ? assignedRounds.items : []);
        this.roundPage = Number(page) || 1;
        this.roundPagination = this.normalizePagination(assignedRounds?.pagination || null, this.roundPage, this.dashboardItems.length || 6);
        this.competitionRounds = Array.isArray(assignedRounds?.competition_rounds) ? assignedRounds.competition_rounds : [];
        this.dashboardScope = assignedRounds?.scope || null;

        if (this.selectedRoundId) {
          await this.fetchViewStats();
        } else {
          this.clearViewStats();
        }

        this.closeFloatingJudgeInfo();
      } catch (error) {
        console.error('❌ Error al obtener dashboard de supervisor:', error);
        this.dashboardSummary = {};
        this.dashboardExtremes = {};
        this.dashboardAssignedJudges = [];
        this.dashboardItems = [];
        this.roundPagination = null;
        this.competitionRounds = [];
        this.dashboardScope = null;
        this.clearViewStats();
        this.closeFloatingJudgeInfo();
        if (!this.judgeOptions.length) {
          await this.fetchJudges();
        }
      } finally {
        this.dashboardLoading = false;
      }
    },
    judgeLabel(judge) {
      if (!judge) return '';
      const parts = [judge.name, judge.surname].filter(Boolean);
      const label = parts.length ? parts.join(' ') : `#${judge.id}`;
      return `${label} (#${judge.id})`;
    },
    judgeName(judge) {
      if (!judge) return '';
      const parts = [judge.name, judge.surname].filter(Boolean);
      return parts.join(' ');
    },
    normalizePagination(rawPagination, fallbackPage = 1, fallbackPerPage = 6) {
      const source = rawPagination && typeof rawPagination === 'object' ? rawPagination : {};
      const currentPage = Number(source.current_page ?? source.currentPage ?? fallbackPage) || fallbackPage;
      const perPage = Number(source.per_page ?? source.perPage ?? fallbackPerPage) || fallbackPerPage;
      const total = Number(source.total ?? source.total_items ?? source.totalItems ?? 0) || 0;
      const count = Number(source.count ?? source.item_count ?? source.itemCount ?? 0) || 0;
      const lastPage = Number(source.last_page ?? source.lastPage ?? (perPage > 0 ? Math.ceil(total / perPage) : 1)) || 1;

      return {
        current_page: Math.max(1, currentPage),
        per_page: Math.max(1, perPage),
        total: Math.max(0, total),
        count: Math.max(0, count),
        last_page: Math.max(1, lastPage),
        has_more_pages: Boolean(source.has_more_pages ?? source.hasMorePages ?? currentPage < lastPage),
        next_page: Number(source.next_page ?? source.nextPage ?? (currentPage < lastPage ? currentPage + 1 : null)) || null,
        prev_page: Number(source.prev_page ?? source.prevPage ?? (currentPage > 1 ? currentPage - 1 : null)) || null,
      };
    },
    resolveRoundVideoLink(round) {
      return String(round?.video_link || round?.video_url || round?.videoUrl || '').trim();
    },
    resolveRoundStatusLabel(round) {
      return round?.status?.name || round?.status_name || round?.status_label || '—';
    },
    resolveRoundRuleLabel(round) {
      if (round?.rule?.name) return round.rule.name;
      if (round?.rule_name) return round.rule_name;

      const ruleId = Number(round?.rule_id || round?.modeProgram || 0);
      if (!ruleId) return '—';

      const matchedRule = this.ruleOptions.find(rule => Number(rule.id) === ruleId);
      return matchedRule?.name || `#${ruleId}`;
    },
    buildCompareGroupLabel(group) {
      const roundName = String(group?.round_name || '').trim();
      const athleteName = String(
        group?.athlete_full_name
        || [group?.athlete_name, group?.athlete_surname].filter(Boolean).join(' ')
      ).trim();
      const fallback = String(group?.label || '').trim() || `${this.$t('training.round_name') || 'Ronda'} #${group?.selector_id || '—'}`;
      const baseLabel = roundName || fallback;

      if (athleteName && !baseLabel.toLowerCase().includes(athleteName.toLowerCase())) {
        return `${baseLabel} · ${athleteName}`;
      }

      return baseLabel;
    },
    buildCompareJudgeOptionLabel(judge, judgeId, roundId) {
      const judgeDisplayName = judge
        ? (this.judgeName(judge) || `#${judge.id}`)
        : (judgeId ? `#${judgeId}` : (this.$t('training.assigned_judge') || 'Juez'));

      return `${judgeDisplayName} - #${roundId}`;
    },
    buildCompareOriginalOptionLabel(item, roundId) {
      const roundName = String(item?.round_name || item?.label || '').trim() || `${this.$t('training.round_name') || 'Ronda'} #${roundId}`;
      const statusLabel = item?.status?.name || item?.status_name || '';
      const scoreLabel = this.formatScore(item?.total_score);
      const parts = [roundName];

      if (statusLabel && statusLabel !== '—') parts.push(statusLabel);
      if (scoreLabel && scoreLabel !== '—') parts.push(scoreLabel);

      return parts.join(' · ');
    },
    buildCompareGroupIdFromSource(item) {
      const selectorId = String(item?.selector_id || '').trim();
      if (selectorId) return selectorId;

      const roundName = String(item?.round_name || item?.label || '').trim().toLowerCase();
      const athleteName = String(
        item?.athlete_full_name
        || [item?.athlete_name, item?.athlete_surname].filter(Boolean).join(' ')
      ).trim().toLowerCase();

      const composite = [roundName, athleteName].filter(Boolean).join('::');
      if (composite) return composite;

      const latestRoundId = Number(item?.latest_round_id || item?.round_id || item?.id || 0);
      return latestRoundId ? String(latestRoundId) : '';
    },
    buildCompareGroupsFromSelectors() {
      return (this.selectorRounds || []).map((group) => {
        const groupId = this.buildCompareGroupIdFromSource(group);
        if (!groupId) return null;

        const referenceRoundId = Number(
          group?.reference_round_id
          || group?.referenceRoundId
          || group?.latest_round_id
          || 0
        ) || null;

        const roundIds = Array.isArray(group?.round_ids) ? group.round_ids : [];
        const judgeIds = Array.isArray(group?.assigned_judge_ids) ? group.assigned_judge_ids : [];
        const judges = Array.isArray(group?.assigned_judges) ? group.assigned_judges : [];
        const optionMap = new Map();

        roundIds.forEach((roundId, index) => {
          const normalizedRoundId = Number(roundId || 0);
          if (!normalizedRoundId) return;

          const judgeId = Number(judgeIds[index] || 0) || null;
          const assignedJudge = judgeId
            ? (judges.find(judge => Number(judge?.id) === judgeId)
              || this.judgeOptions.find(judge => Number(judge?.id) === judgeId)
              || this.dashboardAssignedJudges.find(judge => Number(judge?.id) === judgeId)
              || { id: judgeId, name: '', surname: '' })
            : null;

          optionMap.set(normalizedRoundId, {
            id: normalizedRoundId,
            judgeId,
            label: this.buildCompareJudgeOptionLabel(assignedJudge, judgeId, normalizedRoundId),
          });
        });

        return {
          id: groupId,
          label: this.buildCompareGroupLabel(group),
          referenceRoundId,
          options: Array.from(optionMap.values()),
        };
      }).filter(Boolean);
    },
    buildCompareGroupsFromOriginalRounds() {
      const groupMap = new Map();

      const appendToGroup = (groupLike) => {
        const option = this.normalizeOriginalCompareOption(groupLike?.original_round || null, groupLike);
        if (!option) return;

        const referenceRoundId = Number(option.referenceRoundId || 0);
        if (!referenceRoundId) return;

        const groupId = String(referenceRoundId);

        if (!groupMap.has(groupId)) {
          groupMap.set(groupId, {
            id: groupId,
            label: this.buildCompareGroupLabel(groupLike),
            referenceRoundId,
            options: [],
          });
        }

        const groupEntry = groupMap.get(groupId);
        if (!groupEntry.options.some(entry => Number(entry.id) === Number(option.id))) {
          groupEntry.options.push(option);
        }
      };

      (this.selectorRounds || []).forEach((group) => {
        if (!group || typeof group !== 'object') return;
        appendToGroup(group);
      });

      return Array.from(groupMap.values());
    },
    normalizeOriginalCompareOption(item, group) {
      const roundId = Number(
        item?.round_id
        || item?.id
        || item?.round_id
        || item?.original_round_id
        || group?.original_round_id
        || item?.latest_round_id
        || 0
      );
      if (!roundId) return null;

      const referenceRoundId = Number(
        item?.reference_round_id
        || item?.referenceRoundId
        || group?.reference_round_id
        || group?.referenceRoundId
        || group?.original_round_id
        || item?.round_id
        || roundId
        || 0
      ) || null;
      if (!referenceRoundId) return null;

      return {
        id: roundId,
        judgeId: null,
        label: this.buildCompareOriginalOptionLabel(item, roundId),
        groupId: String(referenceRoundId),
        referenceRoundId,
      };
    },
    normalizeCompareOption(item) {
      const roundId = Number(item?.id || item?.round_id || 0);
      if (!roundId) return null;

      const roundName = String(item?.round_name || '').trim() || `${this.$t('training.round_name') || 'Ronda'} #${roundId}`;
      const assignedJudge = this.resolveCompareJudge(item);
      const judgeId = Number(item?.assigned_judge_id || item?.judge_id || assignedJudge?.id || 0) || null;
      const judgeLabel = assignedJudge
        ? this.judgeLabel(assignedJudge)
        : (judgeId ? `#${judgeId}` : (this.$t('training.assigned_judge') || 'Juez'));
      const statusLabel = item?.status?.name || item?.status_name || '—';
      const scoreLabel = this.formatScore(item?.total_score);

      return {
        id: roundId,
        judgeId,
        label: `${roundName} · ${judgeLabel} · ${statusLabel} · ${scoreLabel}`,
      };
    },
    resolveCompareJudge(item) {
      if (!item) return null;
      if (item.assigned_judge) return item.assigned_judge;
      if (item.judge) return item.judge;

      const judgeId = Number(item.assigned_judge_id || item.judge_id || 0);
      if (!judgeId) return null;

      return this.judgeOptions.find(judge => Number(judge.id) === judgeId)
        || this.dashboardAssignedJudges.find(judge => Number(judge.id) === judgeId)
        || { id: judgeId, name: '', surname: '' };
    },
    formatDuration(seconds) {
      const value = Number(seconds);
      if (!Number.isFinite(value) || value <= 0) return '—';
      const mins = Math.floor(value / 60).toString().padStart(2, '0');
      const secs = Math.floor(value % 60).toString().padStart(2, '0');
      return `${mins}:${secs}`;
    },
    extremeRoundLabel(key, metric) {
      const entry = this.dashboardExtremes?.[key];
      if (!entry) return '—';
      const value = metric === 'duration'
        ? this.formatDuration(entry.duration_seconds ?? entry.value)
        : this.formatScore(entry.total_score ?? entry.value);
      const roundName = entry.round_name || `#${entry.round_id || '—'}`;
      return `${roundName} · ${value}`;
    },
    truncateLabel(text, maxLength = 26) {
      const normalized = String(text || '').trim();
      if (!normalized) return '—';
      return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 3)}...` : normalized;
    },
    extremeJudgeMetricLabel(key, metric) {
      const entry = this.dashboardExtremes?.[key];
      if (!entry) return '—';

      const judge = this.resolveJudgeFromExtremeEntry(entry);
      const judgeName = judge
        ? [judge.name, judge.surname].filter(Boolean).join(' ') || `#${judge.id}`
        : '—';

      const value = metric === 'duration'
        ? this.formatDuration(entry.duration_seconds ?? entry.value)
        : this.formatScore(entry.total_score ?? entry.value);

      return `${this.truncateLabel(judgeName)} · ${value}`;
    },
    resolveJudgeFromExtremeEntry(entry) {
      if (!entry) return null;
      if (entry.judge) return entry.judge;

      const judgeId = entry.assigned_judge_id || entry.judge_id || null;
      if (!judgeId) return null;

      const fromOptions = this.judgeOptions.find(judge => Number(judge.id) === Number(judgeId));
      if (fromOptions) return fromOptions;

      const fromAssigned = this.dashboardAssignedJudges.find(judge => Number(judge.id) === Number(judgeId));
      if (fromAssigned) return fromAssigned;

      return { id: judgeId, name: '', surname: '' };
    },
    openJudgeInfoFromExtreme(key) {
      const entry = this.dashboardExtremes?.[key];
      if (!entry) return;

      const metricType = key.includes('score') ? 'score' : 'duration';
      const metricLabel = metricType === 'score'
        ? this.formatScore(entry.total_score ?? entry.value)
        : this.formatDuration(entry.duration_seconds ?? entry.value);

      const metricTitleByKey = {
        fastest_round: this.$t('training.supervisor.best_duration') || 'Menor duración',
        slowest_round: this.$t('training.supervisor.worst_duration') || 'Mayor duración',
        highest_total_score_round: this.$t('training.supervisor.best_score') || 'Mayor puntaje',
        lowest_total_score_round: this.$t('training.supervisor.worst_score') || 'Menor puntaje',
      };
      const metricTitle = metricTitleByKey[key] || (metricType === 'score' ? (this.$t('training.supervisor.best_score') || 'Mayor puntaje') : (this.$t('training.supervisor.best_duration') || 'Menor duración'));

      const judge = this.resolveJudgeFromExtremeEntry(entry);
      if (!judge) return;

      this.floatingJudgeInfo = {
        judge,
        metricTitle,
        metricLabel,
        round_name: entry.round_name || null,
      };
    },
    closeFloatingJudgeInfo() {
      this.floatingJudgeInfo = null;
    },
    clearViewStats() {
      this.viewStatsSummary = {};
      this.viewStatsTimeline = [];
      this.viewStatsRecent = [];
      this.viewStatsLoading = false;
    },
    async fetchViewStats() {
      if (!this.selectedRoundFilterValue) {
        this.clearViewStats();
        return;
      }

      this.viewStatsLoading = true;
      try {
        const params = {
          entity_type: 'round',
          entity_id: this.selectedRoundFilterValue,
          limit: 100,
        };

        if (this.dashboardFilters.from) params.from = this.dashboardFilters.from;
        if (this.dashboardFilters.to) params.to = this.dashboardFilters.to;
        if (this.dashboardFilters.groupBy) params.group_by = this.dashboardFilters.groupBy;

        const response = await this.$axios.get('/view-stats', {
          params,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        const payload = response?.data || {};
        this.viewStatsSummary = payload?.summary || {};
        this.viewStatsTimeline = Array.isArray(payload?.timeline) ? payload.timeline : [];
        this.viewStatsRecent = Array.isArray(payload?.recent_views) ? payload.recent_views : [];
      } catch (error) {
        console.error('❌ Error al obtener estadísticas de vistas:', error);
        this.clearViewStats();
      } finally {
        this.viewStatsLoading = false;
      }
    },
    formatViewActor(entry) {
      const actor = entry?.user || entry?.viewer || entry?.actor || null;
      if (actor) {
        const name = [actor.name, actor.surname].filter(Boolean).join(' ').trim();
        if (name) return name;
        if (actor.email) return actor.email;
        if (actor.id) return `#${actor.id}`;
      }

      if (entry?.user_name) return entry.user_name;
      if (entry?.email) return entry.email;
      return '—';
    },
    formatViewDate(entry) {
      const raw = entry?.created_at || entry?.viewed_at || entry?.timestamp || null;
      if (!raw) return '—';

      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString();
    },
    normalizeViewControl(round) {
      const fromRound = round?.view_control ?? round?.viewControl ?? null;
      const matchedDashboardItem = (this.dashboardItems || []).find(
        item => Number(item?.id) === Number(round?.id)
      ) || null;
      const fromDashboard = matchedDashboardItem?.view_control ?? matchedDashboardItem?.viewControl ?? null;

      const raw = fromRound ?? fromDashboard;
      if (!raw) return null;

      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw);
        } catch (_) {
          return null;
        }
      }

      if (typeof raw === 'object') return raw;
      return null;
    },
    isTruthyViewFlag(value) {
      if (value === true || value === 1) return true;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return normalized === 'true' || normalized === '1';
      }
      return false;
    },
    hasViewInPhase(control, phase) {
      if (!control) return false;

      const snakeFlag = `has_view_${phase}`;
      const camelPhase = phase.replace(/_([a-z])/g, (_, chr) => chr.toUpperCase());
      const camelFlag = `hasView${camelPhase.charAt(0).toUpperCase()}${camelPhase.slice(1)}`;

      const flagValue = control?.[snakeFlag] ?? control?.[camelFlag];
      if (this.isTruthyViewFlag(flagValue)) return true;

      const phaseData = control?.[phase] || control?.[camelPhase] || null;
      const phaseCount = Number(phaseData?.count || 0);
      if (Number.isFinite(phaseCount) && phaseCount > 0) return true;

      const events = Array.isArray(control?.events) ? control.events : [];
      return events.some(event => String(event?.phase || '').trim() === phase);
    },
    resolveViewPhaseDate(control, phase) {
      if (!control) return '—';

      const camelPhase = phase.replace(/_([a-z])/g, (_, chr) => chr.toUpperCase());
      const phaseData = control?.[phase] || control?.[camelPhase] || {};
      const phaseEvent = (Array.isArray(control?.events) ? control.events : [])
        .find(event => String(event?.phase || '').trim() === phase);

      const rawDate = phaseData?.first_viewed_at
        || phaseData?.last_viewed_at
        || phaseData?.firstViewedAt
        || phaseData?.lastViewedAt
        || phaseEvent?.viewed_at
        || phaseEvent?.viewedAt
        || control?.first_viewed_at
        || control?.last_viewed_at
        || control?.firstViewedAt
        || control?.lastViewedAt
        || null;
      if (!rawDate) return '—';

      const date = new Date(rawDate);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString();
    },
    getViewControlEntries(round) {
      const control = this.normalizeViewControl(round);
      if (!control) return [];

      const phases = [
        {
          key: 'before_assignment',
          label: this.$t('training.supervisor.view_before_assignment') || 'Visualizó antes de asignación',
        },
        {
          key: 'during_exam',
          label: this.$t('training.supervisor.view_during_exam') || 'Visualizó durante examen',
        },
        {
          key: 'after_exam',
          label: this.$t('training.supervisor.view_after_exam') || 'Visualizó después de examen',
        },
        {
          key: 'after_assignment_before_start',
          label: this.$t('training.supervisor.view_after_assignment_before_start') || 'Visualizó ronda oficial publicada',
        },
      ];

      return phases
        .filter(phase => this.hasViewInPhase(control, phase.key))
        .map(phase => ({
          ...phase,
          dateLabel: this.resolveViewPhaseDate(control, phase.key),
        }));
    },
    async selectJudgeFromDashboard(judgeId) {
      const normalizedId = String(judgeId || '');
      if (!normalizedId) return;
      this.selectedJudgeId = normalizedId;
      this.selectedRoundId = '';
      this.roundPage = 1;
      await this.refreshSupervisorData(1);
    },
    async changeVisiblePage(page) {
      const requestedPage = Number(page) || 1;
      if (requestedPage <= 0) return;

      if (!this.selectedJudgeId && !this.selectedRoundId) return;
      if (requestedPage === this.roundPage) return;

      this.roundPage = requestedPage;
      await this.fetchSupervisorDashboard(requestedPage);
    },
    async fetchExternalAthleteOptions() {
      if (this.externalAthletesLoading || this.externalAthleteOptions.length) {
        return;
      }

      this.externalAthletesLoading = true;
      try {
        const response = await this.$axios.get('/athletes/list_short', {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        const rawAthletes = response?.data?.athletes;
        const athletes = Array.isArray(rawAthletes)
          ? rawAthletes
          : (rawAthletes && typeof rawAthletes === 'object' ? Object.values(rawAthletes) : []);

        this.externalAthleteOptions = athletes
          .map((athlete) => {
            const value = athlete?.user_id ?? athlete?.user?.id ?? athlete?.id ?? '';
            const name = String(athlete?.name || '').trim();
            const surname = String(athlete?.surname || '').trim();
            const email = String(athlete?.email || '').trim();
            const fullName = [name, surname].filter(Boolean).join(' ').trim();
            const label = fullName || email || (value ? `#${value}` : '');
            const searchText = [name, surname, fullName, email].filter(Boolean).join(' ');

            if (!value || !label) {
              return null;
            }

            return {
              value: String(value),
              label,
              searchText,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      } catch (error) {
        console.error('❌ Error al obtener atletas para vídeo externo:', error);
        this.externalAthleteOptions = [];
      } finally {
        this.externalAthletesLoading = false;
      }
    },
    openCreateModal() {
      this.openConfirmationModal({
        action: 'create-round',
        message: this.$t('training.supervisor.modal.create_confirm'),
        showCancel: true,
        showRuleSelector: true,
        showNameInput: true,
        showJudgeSelect: true,
        showVideoInput: true,
        showVideoSourceSwitch: true,
        showExternalAthleteSelect: true,
        showRoundSourceSelect: true,
      });
      this.fetchExternalAthleteOptions();
    },
    openCompareModal() {
      this.compareVisible = true;
      this.compareError = '';

      this.ensureCompareSelection();
    },
    closeCompareModal() {
      this.compareVisible = false;
      this.compareLoading = false;
    },
    getCompareSelectionGroups(mode = this.compareMode) {
      if (mode === 'original') {
        const originalGroupMap = new Map(
          this.compareOriginalRoundGroups.map(group => [Number(group.referenceRoundId || 0), group])
        );

        return this.compareRoundGroups
          .map((judgeGroup) => {
            const originalGroup = originalGroupMap.get(Number(judgeGroup.referenceRoundId || 0));
            const leftOptions = Array.isArray(judgeGroup?.options) ? judgeGroup.options : [];
            const rightOptions = Array.isArray(originalGroup?.options) ? originalGroup.options : [];

            if (!leftOptions.length || !rightOptions.length) return null;

            return {
              id: judgeGroup.id,
              label: judgeGroup.label,
              leftOptions,
              rightOptions,
            };
          })
          .filter(Boolean);
      }

      return this.compareRoundGroups
        .filter(group => Array.isArray(group?.options) && group.options.length > 1)
        .map(group => ({
          id: group.id,
          label: group.label,
          leftOptions: group.options,
          rightOptions: group.options,
        }));
    },
    ensureCompareSelection(mode = this.compareMode) {
      const currentLeftId = Number(this.compareSelection.leftRoundId || 0);
      const currentRightId = Number(this.compareSelection.rightRoundId || 0);
      const groups = this.getCompareSelectionGroups(mode);

      const currentGroup = groups.find(group => {
        const leftIds = new Set((group.leftOptions || []).map(option => Number(option.id)));
        const rightIds = new Set((group.rightOptions || []).map(option => Number(option.id)));
        return leftIds.has(currentLeftId) && rightIds.has(currentRightId);
      });

      if (currentGroup) return;

      const defaultGroup = groups.find(group => (group.leftOptions || []).length && (group.rightOptions || []).length) || null;
      if (!defaultGroup) {
        this.compareSelection = {
          leftRoundId: '',
          rightRoundId: '',
          leftJudgeId: null,
          rightJudgeId: null,
        };
        return;
      }

      const leftOption = defaultGroup.leftOptions[0] || null;
      const rightOption = mode === 'judge'
        ? ((defaultGroup.rightOptions || []).find(option => Number(option.id) !== Number(leftOption?.id || 0)) || defaultGroup.rightOptions[0] || null)
        : (defaultGroup.rightOptions[0] || null);

      this.setCompareSide('left', leftOption?.id || '');
      this.setCompareSide('right', rightOption?.id || '');
    },
    setCompareMode(mode) {
      const normalizedMode = mode === 'original' ? 'original' : 'judge';
      if (this.compareMode === normalizedMode) return;

      this.compareMode = normalizedMode;
      this.compareError = '';
      this.comparePayload = null;

      if (normalizedMode === 'original') {
        this.compareSelection = {
          ...this.compareSelection,
          rightJudgeId: null,
        };
      }

      this.ensureCompareSelection(normalizedMode);
    },
    setCompareSide(side, value) {
      const normalizedId = Number(value || 0);
      const sourceOptions = side === 'right' && this.compareMode === 'original'
        ? this.compareOriginalRoundOptions
        : this.compareRoundOptions;
      const option = sourceOptions.find(entry => Number(entry.id) === normalizedId) || null;
      const roundKey = `${side}RoundId`;
      const judgeKey = `${side}JudgeId`;

      this.compareSelection = {
        ...this.compareSelection,
        [roundKey]: normalizedId || '',
        [judgeKey]: this.compareMode === 'original' && side === 'right'
          ? null
          : (option?.judgeId || null),
      };
      this.compareError = '';
      this.comparePayload = null;
    },
    async fetchCompareRounds() {
      const leftRoundId = Number(this.compareSelection.leftRoundId || 0);
      const rightRoundId = Number(this.compareSelection.rightRoundId || 0);

      if (!leftRoundId || !rightRoundId) {
        this.compareError = this.$t('training.supervisor.compare_missing_selection') || 'Selecciona una ronda para cada lado.';
        return;
      }

      if (leftRoundId === rightRoundId) {
        this.compareError = this.$t('training.supervisor.compare_same_round') || 'Selecciona dos rondas diferentes para comparar.';
        return;
      }

      this.compareLoading = true;
      this.compareError = '';

      try {
        const payload = {
          left_round_id: leftRoundId,
          right_round_id: rightRoundId,
        };

        if (this.compareSelection.leftJudgeId) payload.left_judge_id = this.compareSelection.leftJudgeId;
        if (this.compareMode === 'judge' && this.compareSelection.rightJudgeId) {
          payload.right_judge_id = this.compareSelection.rightJudgeId;
        }

        const response = await this.$axios.post('/training/compare-rounds', payload, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (!response?.data?.success) {
          throw new Error(this.$t('training.supervisor.compare_error') || 'No se pudo cargar la comparación.');
        }

        this.comparePayload = response.data;
      } catch (error) {
        console.error('❌ Error al comparar rondas:', error);
        this.comparePayload = null;
        this.compareError = error?.response?.data?.message || this.$t('training.supervisor.compare_error') || 'No se pudo cargar la comparación.';
      } finally {
        this.compareLoading = false;
      }
    },
    getDefaultConfirmationModalConfig() {
      return {
        showCancel: true,
        showRuleSelector: false,
        showNameInput: false,
        showJudgeSelect: false,
        showVideoInput: false,
        showVideoSourceSwitch: false,
        showExternalAthleteSelect: false,
        showRoundSourceSelect: false,
      };
    },
    resetConfirmationModalState() {
      this.pendingModalAction = null;
      this.pendingModalPayload = null;
      this.modalMessage = '';
      this.confirmationModalConfig = this.getDefaultConfirmationModalConfig();
    },
    openConfirmationModal({ action, message, payload = null, ...config }) {
      this.pendingModalAction = action;
      this.pendingModalPayload = payload;
      this.modalMessage = message;
      this.confirmationModalConfig = {
        ...this.getDefaultConfirmationModalConfig(),
        ...config,
      };
      this.$refs.confirmationModal.openModal();
    },
    openInfoModal(message) {
      this.openConfirmationModal({
        action: 'info',
        message,
        showCancel: false,
      });
    },
    async handleConfirmed({ selectedRule, customName, judgeIds, selectedRoundItem, videoSource, videoLink, userId }) {
      const action = this.pendingModalAction;
      const payload = this.pendingModalPayload;
      this.resetConfirmationModalState();

      if (action === 'delete-round') {
        await this.confirmDeleteRound(payload?.id);
        return;
      }

      if (action !== 'create-round') {
        return;
      }

      const selectedIds = (judgeIds || []).map(id => Number(id)).filter(Boolean);
      const selectedSourceRoundId = Number(selectedRoundItem?.id || 0);
      const selectedSourceUserId = Number(selectedRoundItem?.user_id || 0) || null;
      const selectedExternalUserId = Number(userId || 0) || null;
      const normalizedVideoSource = videoSource === 'external' ? 'external' : 'local';
      const normalizedVideoLink = String(videoLink || '').trim();

      if (!selectedIds.length) {
        this.openInfoModal(this.$t('training.supervisor.modal.judge_required'));
        return;
      }

      if (normalizedVideoSource === 'local' && !selectedSourceRoundId) {
        this.openInfoModal(this.$t('training.supervisor.modal.round_source_required') || 'Selecciona una ronda/atleta de origen');
        return;
      }

      if (normalizedVideoSource === 'external' && !normalizedVideoLink) {
        this.openInfoModal(this.$t('training.supervisor.modal.video_required') || 'Introduce una URL de vídeo externa válida');
        return;
      }

      if (normalizedVideoSource === 'local' && !selectedRoundItem?.video_link) {
        this.openInfoModal(this.$t('training.supervisor.modal.video_required') || 'La ronda seleccionada no contiene video_link');
        return;
      }

      this.isSubmitting = true;
      try {
        await Promise.all(selectedIds.map(async id => {
          const payload = {
            training_type: 'judge',
            video_source: normalizedVideoSource,
            user_id: normalizedVideoSource === 'external' ? selectedExternalUserId : selectedSourceUserId,
            assigned_judge_id: id,
            modeProgram: Number(selectedRule),
            round_name: customName || null,
          };

          if (normalizedVideoSource === 'local') {
            payload.reference_round_id = selectedSourceRoundId;
          } else {
            payload.video_url = normalizedVideoLink;
          }

          await this.$axios.post('/rounds', {
            ...payload,
          }, {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          });
        }));
        this.openInfoModal(this.$t('training.supervisor.modal.success'));
        await this.refreshSupervisorData(1);
      } catch (error) {
        console.error('❌ Error al crear entrenamiento de juez:', error);
        this.openInfoModal(this.$t('training.supervisor.modal.error'));
      } finally {
        this.isSubmitting = false;
      }
    },
    formatScore(score) {
      const value = parseFloat(score);
      if (!Number.isFinite(value) || value === 0) return '—';
      return value.toFixed(2);
    },
    getStatusClass(statusId) {
      switch (statusId) {
        case 3:
          return 'status-pending';
        case 1:
          return 'status-registered';
        case 4:
          return 'status-approved';
        case 10:
          return 'status-published';
        case 15:
          return 'status-started';
        case 8:
          return 'status-finished';
        default:
          return 'status-unknown';
      }
    },
    async viewRound(id) {
      try {
        await this.setTrainingRoundsIdToView(id);
        this.updateAppNavigationZone({
          zone: 'training',
          data: { cameFromTrainingZone: true },
        });
        await router.push('/score');
      } catch (error) {
        console.error('❌ Error al ver ronda (supervisor):', error);
      }
    },
    async editRound(id) {
      try {
        await this.setTrainingRoundsIdToEdit(id);
        this.updateAppNavigationZone({
          zone: 'training',
          data: { cameFromTrainingZone: true },
        });
        await router.push('/score');
      } catch (error) {
        console.error('❌ Error al editar ronda (supervisor):', error);
      }
    },
    async deleteRound(id) {
      this.openConfirmationModal({
        action: 'delete-round',
        message: this.$t('training.delete_confirm') || 'Delete this round?',
        payload: { id },
        showCancel: true,
      });
    },
    async confirmDeleteRound(id) {
      if (!id) return;
      this.isLoading = true;
      try {
        await this.$axios.delete(`/delete_round/${id}`, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        await this.refreshSupervisorData(this.roundPage || 1);
      } catch (error) {
        console.error('❌ Error al eliminar ronda (supervisor):', error);
      } finally {
        this.isLoading = false;
      }
    },
    cancelModal() {
      this.resetConfirmationModalState();
    }
  }
};
</script>

<style scoped>
.box_component {
  width: 80%;
  max-width: 80%;
  margin: 15px auto;
}

@media (max-width: 576px) {
  .box_component {
    width: 100%;
    max-width: 100%;
  }
}
.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.panel-title {
  margin: 0;
  font-weight: 600;
  color: #374151;
}
.muted {
  color: #6b7280;
  font-size: 13px;
}

.select {
  min-width: 220px;
}

.new-round-btn {
  background: #1d4ed8;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.compare-rounds-btn {
  background: #0f766e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.compare-rounds-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.compare-rounds-btn:not(:disabled):hover {
  background: #115e59;
}

.new-round-btn:hover {
  background: #1e40af;
}

.training-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 3fr));
  gap: 15px;
  justify-content: center;
  margin: 0 auto;
}

.rounds-pagination {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pagination-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
}

.pagination-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pagination-summary {
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
}

.filter-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 12px 0;
  flex-wrap: wrap;
}

.btn-clear-filters {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
}

.dashboard-panel {
  margin: 12px 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
}

.dashboard-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dashboard-title {
  margin: 0;
  color: #374151;
}

.dashboard-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.dashboard-input {
  min-width: 130px;
}

.dashboard-select {
  min-width: 110px;
}

.btn-refresh {
  background: #0f766e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  font-weight: 600;
  cursor: pointer;
}

.summary-grid {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.active-filters {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.active-filter-chip {
  background: #fff;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 999px;
  font-size: 12px;
  padding: 4px 10px;
}

.summary-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-card.success {
  border-color: #bbf7d0;
}

.summary-card.warning {
  border-color: #fde68a;
}

.summary-card.pending {
  border-color: #fecaca;
}

.summary-label {
  font-size: 12px;
  color: #6b7280;
}

.views-block {
  margin-top: 10px;
}

.views-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 8px;
}

.views-two-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.views-subtitle {
  margin: 0 0 6px;
}

.views-list-scroll {
  max-height: 170px;
  overflow-y: auto;
  padding-right: 4px;
}

.views-line {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 8px;
  margin-bottom: 6px;
  font-size: 12px;
  color: #374151;
  background: #fff;
}

.dashboard-main-row {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.dashboard-block {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
}

.dashboard-block h6 {
  margin: 0 0 8px;
  color: #374151;
}

.assigned-judges-block {
  margin-top: 0;
}

.selected-judge-stats {
  margin-top: 0;
}

.selected-stats-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}

.selected-stat-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  background: #fff;
}

.selected-stat-item span {
  font-size: 12px;
  color: #6b7280;
}

.extreme-trigger {
  border: none;
  background: transparent;
  text-align: right;
  font-weight: 700;
  color: #374151;
  cursor: pointer;
  padding: 0;
}

.extreme-trigger:hover {
  opacity: 0.8;
}

.assigned-judges-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}

.assigned-judge-chip {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.assigned-judge-chip:hover {
  opacity: 0.85;
}

.assigned-judge-chip__name {
  min-width: 0;
}

.assigned-judge-chip__stats {
  flex-shrink: 0;
  font-weight: 700;
  color: #0f766e;
}

.assigned-judge-chip--complete .assigned-judge-chip__stats {
  color: #15803d;
}

.assigned-judge-chip--pending .assigned-judge-chip__stats {
  color: #b91c1c;
}

.round-assigned-judges-list {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 0;
}

.assigned-judge-pill {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(92px, 0.8fr) minmax(92px, 0.8fr);
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f8fafc;
  border-radius: 12px;
  padding: 8px;
  color: #374151;
  font-size: 12px;
}

.assigned-judge-pill__block {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.assigned-judge-pill__identity {
  justify-content: space-between;
  gap: 10px;
}

.assigned-judge-pill__name {
  font-weight: 700;
}

.assigned-judge-pill__id {
  color: #6b7280;
  white-space: nowrap;
}

.assigned-judge-pill__value {
  justify-content: center;
  font-weight: 700;
}

@media (max-width: 576px) {
  .assigned-judge-pill {
    grid-template-columns: 1fr;
  }

  .assigned-judge-pill__identity {
    flex-direction: column;
    align-items: flex-start;
  }
}

.assigned-judge-pill__meta {
  white-space: nowrap;
}

.floating-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}

.floating-panel {
  width: min(420px, calc(100% - 24px));
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.floating-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
}

.floating-header h6 {
  margin: 0;
  color: #374151;
}

.floating-close {
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
}

.floating-body {
  padding: 12px;
}

.floating-body p {
  margin: 0 0 8px;
  color: #374151;
  font-size: 13px;
}

.input {
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  flex: 1;
  min-width: 180px;
}

.training-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  text-align: center;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: box-shadow 0.3s ease;
  margin-bottom: 15px;
  width: 280px;
  cursor: pointer;
}

.status-pending {
  box-shadow: 0 0 10px rgba(255, 165, 0, 0.6);
}
.status-approved {
  box-shadow: 0 0 10px rgba(0, 128, 0, 0.6);
}
.status-published {
  box-shadow: 0 0 10px rgba(0, 0, 255, 0.6);
}
.status-started {
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
}
.status-finished {
  box-shadow: 0 0 10px rgba(128, 128, 128, 0.6);
}
.status-registered {
  box-shadow: 0 0 10px rgba(66, 60, 98, 0.6);
}
.status-unknown {
  box-shadow: 0 0 10px rgba(145, 23, 23, 0.6);
}

.card-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  opacity: 0.7;
}

.card-body {
  padding: 10px 12px 14px;
}

.status-text {
  margin: 0 0 6px;
  font-weight: 600;
  color: #374151;
}

.card-body p {
  margin: 0 0 4px;
  color: #6b7280;
  font-size: 13px;
}

.view-control-info {
  color: #065f46 !important;
}

.view-control-list {
  margin-top: 6px;
  border-top: 1px dashed #bbf7d0;
  padding-top: 6px;
}

.card-body strong {
  color: #374151;
}

.card-footer {
  display: flex;
  justify-content: space-around;
  padding: 10px;
  background: #f8f9fa;
  border-top: 1px solid #ddd;
}

.action-btn {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 5px;
  color: #5b5b5b;
}

.action-btn:hover {
  opacity: 0.7;
}

@media (max-width: 900px) {
  .dashboard-main-row {
    grid-template-columns: 1fr;
  }

  .views-two-cols {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .header-title,
  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .training-card {
    width: 320px;
    margin: 0 auto;
  }
}
</style>
